import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';


import {
	useCustomDataStoringStates,
	useCustomLoadingStates,
	useCustomNullStates
} from '../../hooks/customStates';

import { subscribeGroupMessages, subscribeSupportChatEvents, subScribeChatNotifications, SUPPORT_CHAT_OPEN_EVENT } from './helper';
import { requestBrowserNotificationPermission } from '../../utils/miscellaneous/browserNotification';

import {
	ContactIcon,
	DownwardIcon,
	MessageIcon,
	SupportIcon,
	UpWardIcon
} from '../../assets/svg/icons';
import { LifeBuoy, MessageSquareMore, Plus, RefreshCcw, Search, Video } from 'lucide-react';

import {
	closeChatboxOne,
	closeOverlay,
	playMessageSound
} from '../../assets/js/custome';

import { RecentChats } from './recentChats';
import SupportChatPanel from '../supportChat/SupportChatPanel';
import { supportChatApi } from '../supportChat/supportChatApi';
import { ContactList } from './contactList';

import { useCustomDispatch } from '../../hooks';
import { initiatedChatGroup } from '../../services/actions/chat/initiateChatGroupActions';

import { useCustomSelectors } from '../../services/selectors/allSelectors';
import { initatedGroupId } from '../../storage';

import { initTabs } from 'flowbite';
import debounce from 'lodash/debounce';
import Draggable from 'react-draggable';

import { fetchAllRecentChats } from '../../services/actions/chat/recentChatsListsActions';
import { setPortalChatOpen } from '../../services/chat/chatRefreshGate';
import { FetchPharmacyChatUsers } from '../../services/actions/chat/pharmacyChatUsers';

import CreateChatGroup from './createChatGroup';
import Cookies from "js-cookie";

import * as audio from '../../assets/images/index';
import { addUserActivityStatus } from '../../services/actions/chat/activityStatusActions';
import PharmacistDriverVideoCallManager from '../pharmacist-driver-video-call/PharmacistDriverVideoCallManager';

const getInitials = (value) => {
	const name = String(value || '').trim();
	if (!name) return 'U';

	const parts = name.split(/\s+/).filter(Boolean);
	if (parts.length === 1) {
		return parts[0].slice(0, 2).toUpperCase();
	}

	return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const isValidImageUrl = (value) => {
	const imageUrl = String(value || '').trim();
	return imageUrl && !['null', 'undefined'].includes(imageUrl.toLowerCase());
};

const ChatBox = () => {

	const chatGroupID = initatedGroupId()
	const dispatch = useCustomDispatch()

	const {
		profileData, recentChatList,
		pharmacyLoginData,
		authData,
		assignchat,
		pharmacyId,
		teamStatus } = useCustomSelectors()

	const {
		selectedEmployeeId,
		setSelectedEmployeeId } = useCustomNullStates()

	const { isChatboxVisible,
		setIsChatboxVisible,
		chatLoading,
		setChatLoading,
		recentChatLoading,
		setRecenetChatLoading,
		setIsChatOpen
	} = useCustomLoadingStates()

	const {
		chatAppenedMessages, setChatAppenedMessages,
		myInputMessages, setMyInputMessages,
		allGroupMessages, setAllGroupMessages
	} = useCustomDataStoringStates()

	const {
		employeesOnlineStatus,
		setEmployeesOnlineStatus,
		overlayImage,
		setOverlayImage
	} = useCustomNullStates()

	const [chatInput, setChatInput] = useState('')
	const [page, setPage] = useState(2)
	const [callAssistantRefreshKey, setCallAssistantRefreshKey] = useState(0)
	const [isAssistantVideoCallActive, setIsAssistantVideoCallActive] = useState(false)
	const [headerAvatarFailed, setHeaderAvatarFailed] = useState(false)
	const [supportUnreadCount, setSupportUnreadCount] = useState(0);
	const [supportUnreadThreadIds, setSupportUnreadThreadIds] = useState([]);
	const [launcherPosition, setLauncherPosition] = useState({ x: 0, y: 0 });
	const launcherNodeRef = useRef(null);
	const launcherDraggedRef = useRef(false);

	const loginUser = authData?.data?.pharmacyloginuser?.[0] || {};
	const isLoggedInPharmacist = String(profileData?.type || loginUser?.type || '').trim().toLowerCase() === 'pharmacist';
	const canUseAssignedChatTabs = Boolean(assignchat);
	const headerAvatarUrl = isValidImageUrl(profileData?.thumbnail) ? profileData.thumbnail : '';
	const headerDisplayName = profileData?.name
		|| profileData?.full_name
		|| loginUser?.name
		|| profileData?.email
		|| loginUser?.email
		|| profileData?.username
		|| profileData?.user_name
		|| loginUser?.username
		|| loginUser?.user_name;
	const shouldShowHeaderAvatarImage = headerAvatarUrl && !headerAvatarFailed;

	useEffect(() => {
		initTabs()
	}, [profileData])

	// Presence events may arrive anywhere in the portal. Let them refresh the
	// recent-chat list only while this panel is actually open.
	useEffect(() => {
		setPortalChatOpen(isChatboxVisible);
		return () => setPortalChatOpen(false);
	}, [isChatboxVisible]);

	useEffect(() => {
		setHeaderAvatarFailed(false);
	}, [headerAvatarUrl])

	const loadSupportUnreadCount = useCallback(async () => {
		try {
			const response = await supportChatApi.getUnreadCount();
			setSupportUnreadCount(Number(response?.unread_count || 0));
			setSupportUnreadThreadIds(Array.isArray(response?.unread_thread_ids) ? response.unread_thread_ids : []);
		} catch (error) {
			setSupportUnreadCount(0);
			setSupportUnreadThreadIds([]);
		}
	}, []);

	const openChat = useCallback(() => {
		setIsChatOpen((current) => !current);
		setRecenetChatLoading(true)
		dispatch(addUserActivityStatus('online'));
		dispatch(FetchPharmacyChatUsers(null, setChatLoading))
		dispatch(fetchAllRecentChats(setRecenetChatLoading))
	}, [dispatch, setChatLoading, setIsChatOpen, setRecenetChatLoading]);

	const toggleChatbox = () => {
		if (launcherDraggedRef.current) {
			launcherDraggedRef.current = false;
			return;
		}

		if (!isChatboxVisible) {
			openChat()
			loadSupportUnreadCount()
		}
		setIsChatboxVisible(!isChatboxVisible)
		setSelectedEmployeeId(null)
	}

	function openChatbox(value, type) {

		const clearState = () => {
			Cookies.remove('previous');
			Cookies.remove('initated');
			closeChatboxOne();
			setAllGroupMessages([]);
			setEmployeesOnlineStatus(value);
			setChatInput('');
			setChatAppenedMessages([])
			setMyInputMessages([]);
		};

		if (type === 'contact_list') {
			const { id } = value;
			clearState();
			dispatch(initiatedChatGroup('one_to_one', [id]));
			setSelectedEmployeeId(id);
		}

		if (type === 'recent_chat_list') {
			const { user_id } = value;
			clearState();
			dispatch(initiatedChatGroup('one_to_one', [user_id?.[0]]));
			setSelectedEmployeeId(user_id?.[0]);
		}
		setPage(2)
	}

	const searchRecentChatList = useCallback(
		debounce((e) => {
			const search = e.target.value
			setRecenetChatLoading(true)
			dispatch(fetchAllRecentChats(setRecenetChatLoading, search))
		}, 500),
		[dispatch]
	)

	const searchContactList = useCallback(
		debounce((e) => {
			const search = e.target.value
			setChatLoading(true)
			dispatch(FetchPharmacyChatUsers(search, setChatLoading))
		}, 500),
		[dispatch]
	)

	function openOverlay(image) {
		document.getElementById('overlay').style.display = 'flex';
		setOverlayImage(image)
	}

	const handleRemoveData = (Cookies) => {
		setSelectedEmployeeId(null)
		Cookies.remove('initated')
		Cookies.remove('previous')
	}

	const recentChatsResponse = recentChatList?.data?.latest_messages?.reduce((acc, value, index) => {
		return acc + Number(value.unread_count || 0);
	}, 0);
	const chatUnreadCount = Number(recentChatsResponse || 0);
	const totalUnreadCount = chatUnreadCount + Number(supportUnreadCount || 0);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.altKey && event.key === 'c') {
				setIsChatboxVisible(prevState => !prevState);
				return;
			}

			if (event.key === 'Escape') {
				setIsChatboxVisible(false);
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		}
	}, []);

	const [activeTab, setActiveTab] = useState('chats');
	const [supportToolbar, setSupportToolbar] = useState(null);
	const [supportRefreshToken, setSupportRefreshToken] = useState(0);
	const [isSupportTabAvailable, setIsSupportTabAvailable] = useState(true);

	const tabLabels = {
		chats: 'Messaging',
		contact: 'Contacts',
		support: 'Support',
		callAssistant: 'Call Assistant',
	};

	const visibleTabs = useMemo(() => ([
		...(canUseAssignedChatTabs ? ['chats', 'contact'] : []),
		...(isSupportTabAvailable ? ['support'] : []),
		...(isLoggedInPharmacist ? ['callAssistant'] : []),
	]), [canUseAssignedChatTabs, isLoggedInPharmacist, isSupportTabAvailable]);

	const tabColor = (tabName) => (activeTab === tabName ? '#2626E0' : '#757575');

	const supportDetailOpen = activeTab === 'support' && supportToolbar?.isDetailView;
	const chatDetailOpen = (activeTab === 'chats' || activeTab === 'contact') && Boolean(selectedEmployeeId);
	const detailOpen = supportDetailOpen || chatDetailOpen;
	const chatboxPositionStyle = useMemo(() => {
		const baseRight = 16;
		const popupWidth = 340;
		const popupHeight = 506;
		const viewportMargin = 12;
		const maxRight = typeof window === 'undefined'
			? Number.POSITIVE_INFINITY
			: Math.max(viewportMargin, window.innerWidth - popupWidth - viewportMargin);
		const maxBottom = typeof window === 'undefined'
			? Number.POSITIVE_INFINITY
			: Math.max(0, window.innerHeight - popupHeight - viewportMargin);

		return {
			right: `${Math.min(Math.max(baseRight - Math.round(launcherPosition.x), viewportMargin), maxRight)}px`,
			bottom: `${Math.min(Math.max(Math.round(-launcherPosition.y), 0), maxBottom)}px`,
		};
	}, [launcherPosition.x, launcherPosition.y]);

	useEffect(() => {
		if (!visibleTabs.includes(activeTab)) {
			setActiveTab(visibleTabs[0] || 'support');
		}
	}, [activeTab, visibleTabs]);

	useEffect(() => {
		if (!pharmacyId) return undefined;

		loadSupportUnreadCount();

		const subscription = subscribeSupportChatEvents(pharmacyId, (eventData) => {
			if (eventData?.event_type === 'message.agent_reply') {
				if (eventData?.unread_count !== undefined && eventData?.unread_count !== null) {
					setSupportUnreadCount(Number(eventData.unread_count || 0));
				} else {
					setSupportUnreadCount((current) => current + 1);
				}
				if (Array.isArray(eventData?.unread_thread_ids)) {
					setSupportUnreadThreadIds(eventData.unread_thread_ids);
				} else if (eventData?.thread_id) {
					const eventThreadId = String(eventData.thread_id);
					setSupportUnreadThreadIds((current) => (
						current.map((threadId) => String(threadId)).includes(eventThreadId) ? current : [...current, eventThreadId]
					));
				}
			}
			setSupportRefreshToken((current) => current + 1);
		});

		return () => {
			try {
				subscription?.close?.();
			} catch (error) {
				console.error('Error closing support chat websocket subscription:', error);
			}
		};
	}, [pharmacyId, loadSupportUnreadCount]);

	useEffect(() => {
		const handleOpenSupportChat = () => {
			if (!isSupportTabAvailable) return;

			setIsChatboxVisible(true);
			setActiveTab('support');
			setSupportRefreshToken((current) => current + 1);
		};

		window.addEventListener(SUPPORT_CHAT_OPEN_EVENT, handleOpenSupportChat);
		return () => window.removeEventListener(SUPPORT_CHAT_OPEN_EVENT, handleOpenSupportChat);
	}, [isSupportTabAvailable, setIsChatboxVisible]);

	const handleTabClick = (tabName) => {
		if (tabName === 'callAssistant' && !isLoggedInPharmacist) return;
		if (tabName !== activeTab) {
			handleRemoveData(Cookies);
		}
		if (tabName === 'support') {
			loadSupportUnreadCount();
			setSupportRefreshToken((current) => current + 1);
		}
		setActiveTab(tabName);
		if (tabName === 'callAssistant') {
			setCallAssistantRefreshKey((key) => key + 1);
		}
	};

	const handleSearchChange = (event) => {
		if (activeTab === 'chats') {
			searchRecentChatList(event);
			return;
		}

		if (activeTab === 'contact') {
			searchContactList(event);
		}
	};

	useEffect(() => {
		if (!chatGroupID) return;

		// Remove the unused Ably instance - subscribeGroupMessages creates its own
		const ablyConnection = subscribeGroupMessages(
			chatGroupID, 
			setChatAppenedMessages, 
			setMyInputMessages, 
			selectedEmployeeId
		);

		return () => {
			if (ablyConnection) {
				try {
					ablyConnection.close();
				} catch (error) {
					console.error('Error closing Ably connection:', error);
				}
			}
		};
	}, [chatGroupID, selectedEmployeeId]) // Remove setState functions from dependencies

	const enableNotifications = () => {
		requestBrowserNotificationPermission();
	}

	useEffect(() => {
		if (process.env.NODE_ENV === 'production') {
			enableNotifications();
		}
	}, [])

	useEffect(() => {
		const handleAssistantVideoCallActive = (event) => {
			setIsAssistantVideoCallActive(!!event?.detail?.active);
		};

		window.addEventListener('pharmacist-driver-video-call-active', handleAssistantVideoCallActive);
		return () => {
			window.removeEventListener('pharmacist-driver-video-call-active', handleAssistantVideoCallActive);
		};
	}, [])

	useEffect(() => {
		if (!pharmacyId || !profileData?.user_id) return;

		const ablyConnection = subScribeChatNotifications(
			pharmacyId, 
			profileData.user_id, 
			dispatch, 
			playMessageSound
		);

		return () => {
			if (ablyConnection) {
				try {
					ablyConnection.close();
				} catch (error) {
					console.error('Error closing Ably connection:', error);
				}
			}
		};
	}, [pharmacyId, profileData?.user_id, dispatch]) // Use specific values, not entire object

	return (
		<>
			<section
				className={`chatbox z-10 js-chatbox grey-color shadow-2xl hidden md:hidden sm:hidden lg:block ${isChatboxVisible ? 'chatbox--is-visible' : 'chatbox--is-hidden'} ${!detailOpen ? 'chatbox--compact' : ''} ${detailOpen ? 'chatbox--detail' : ''} ${activeTab === 'support' ? 'chatbox--support-compact' : ''} ${supportDetailOpen ? 'chatbox--support-detail' : ''}`}
				style={{
					border: '1px solid #e1e1e1',
					borderRadius: '10px',
					marginBottom: 0,
					zIndex: 999999999999999,
					background: 'white',
					...chatboxPositionStyle,
					display: isAssistantVideoCallActive ? 'none' : undefined
				}}
			>

				<div>
					{!detailOpen && (
					<div className='border border-b-none rounded-md' style={{
						background: '#2626E0',
						color: 'white',
						fontWeight: '500',
						borderBottom: 'none',
						borderBottomLeftRadius: '0px',
						borderBottomRightRadius: '0px'
					}}>

						{activeTab === 'support' ? (
							!supportDetailOpen && (
							<div
								className="chatbox__header h-14 bg-white btnnn z-30 flex items-center gap-1.5 px-2"
								style={{
									background: '#2626E0',
									color: 'white',
									fontWeight: '500',
								}}
								onClick={toggleChatbox}
							>
								<h3 className="flex items-center gap-1.5 text-white rubik-500 fs-16 flex-shrink-0">
									<div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
										<LifeBuoy size={16} />
									</div>
									<span className="whitespace-nowrap text-sm font-medium">Support</span>
								</h3>

								<div className="support-chat-header-toolbar support-chat-header-toolbar--inline" onClick={(event) => event.stopPropagation()}>
									<button
										type="button"
										className="support-chat-header-new-button w-fit"
										onClick={() => supportToolbar?.onNewThread?.()}
									>
										<Plus size={14} />
										<span>New Ticket</span>
									</button>
									<button
										type="button"
										className="support-chat-header-refresh-button"
										onClick={() => supportToolbar?.onRefresh?.()}
										title="Refresh"
										aria-label="Refresh tickets"
									>
										<RefreshCcw size={13} />
									</button>
								</div>

								<button className="js-chatbox-toggle flex items-center flex-shrink-0" onClick={(event) => event.stopPropagation()}>
									<label onClick={toggleChatbox} className='btnnn'>
										{isChatboxVisible ? <DownwardIcon /> : <UpWardIcon />}
									</label>
								</button>
							</div>
							)
						) : (
							<>
								<div
									className="chatbox__header chatbox__header--compact bg-white btnnn z-30"
									style={{
										background: '#2626E0',
										color: 'white',
										fontWeight: '500'
									}}
									onClick={toggleChatbox}
								>

									<h3 className="flex items-center gap-2 text-white rubik-500 fs-16 min-w-0">
										<div className="relative">
											{shouldShowHeaderAvatarImage ? (
												<img
													className="w-8 h-8 rounded-full bg-light object-cover"
													src={headerAvatarUrl}
													onError={() => setHeaderAvatarFailed(true)}
													alt="Rounded avatar"
												/>
											) : (
												<span className="flex w-8 h-8 items-center justify-center rounded-full bg-white/20 text-xs font-semibold text-white ring-1 ring-white/40">
													{getInitials(headerDisplayName)}
												</span>
											)}
											<span className="bottom-0 left-6 absolute w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></span>
										</div>
										<span className="truncate text-sm font-medium">{tabLabels[activeTab] || 'Messaging'}</span>
									</h3>

									<button className="js-chatbox-toggle flex items-center flex-shrink-0" onClick={(event) => event.stopPropagation()}>
										<label onClick={toggleChatbox} className='btnnn'>
											{isChatboxVisible ? <DownwardIcon /> : <UpWardIcon />}
										</label>
									</button>

								</div>

								{(activeTab === 'chats' || activeTab === 'contact') && (
									<div className='chatbox-search-shell sticky w-full z-10 ot_searchBox support-chat-search-wrap'>
										<div className="support-chat-search">
											<Search size={12} className="support-chat-search-icon" aria-hidden="true" />
											<input
												onChange={handleSearchChange}
												autoComplete='off'
												className="chatbox-search-input support-chat-search-input focus:ring-0 focus:border-0 focus:outline-none"
												id="username"
												type="text"
												placeholder="Search"
											/>
										</div>
									</div>
								)}
							</>
						)}
					</div>
					)}

					<div className={`scroll-box w-full js-chatbox-display chatbox__display ${activeTab === 'support' ? 'chatbox__display--support' : ''}`} style={{ borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px' }}>
						{!detailOpen && (
						<div className="bg-white fixed w-full bottom-0 z-10 " style={{ maxWidth: '100%' }}>

							<div className="border-t border-gray-200 dark:border-gray-700 w-full chatbox-tabs--compact">
								<ul className="flex -mb-px text-sm font-medium text-center" id="default-tab" role="tablist">
									{canUseAssignedChatTabs && (
									<li className="flex flex-1 justify-center" role="presentation">
										<button
											className="w-full flex flex-col items-center justify-center p-4 rounded-t-lg"
											id="profile-tab"
											type="button"
											role="tab"
											aria-controls="profile"
											aria-selected={activeTab === 'chats'}
											onClick={() => handleTabClick('chats')}
										>
											<MessageIcon className="mb-2" color={tabColor('chats')} />
											<span style={{ color: tabColor('chats') }}>Chats</span>
										</button>
									</li>
									)}

									{canUseAssignedChatTabs && (
									<li className="flex flex-1 justify-center" role="presentation" onClick={() => handleRemoveData(Cookies)}>
										<button
											className="w-full flex flex-col items-center justify-center p-4 rounded-t-lg"
											id="contact-tab"
											type="button"
											role="tab"
											aria-controls="contact"
											aria-selected={activeTab === 'contact'}
											onClick={() => handleTabClick('contact')}
										>
											<ContactIcon className="mb-2" color={tabColor('contact')} />
											<span style={{ color: tabColor('contact') }}>Contact</span>
										</button>
									</li>
									)}

									{isSupportTabAvailable && (
									<li className="flex flex-1 justify-center" role="presentation" onClick={() => handleRemoveData(Cookies)}>
										<button
											className="w-full flex flex-col items-center justify-center p-4 rounded-t-lg"
											id="support-tab"
											type="button"
											role="tab"
											aria-controls="support"
											aria-selected={activeTab === 'support'}
											onClick={() => handleTabClick('support')}
										>
											<span className="relative mb-2 inline-flex h-[22px] w-[22px] items-center justify-center">
												<SupportIcon color={tabColor('support')} />
												{supportUnreadCount > 0 && (
													<span className="pointer-events-none absolute -right-2 -top-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold leading-none text-white shadow-sm">
														{supportUnreadCount > 99 ? '99+' : supportUnreadCount}
													</span>
												)}
											</span>
											{supportUnreadCount > 0 && (
												<span className="sr-only">{supportUnreadCount} unread support messages</span>
											)}
											<span style={{ color: tabColor('support') }}>Support</span>
										</button>
									</li>
									)}

									{isLoggedInPharmacist && (
										<li className="flex flex-1 justify-center" role="presentation" onClick={() => handleRemoveData(Cookies)}>
											<button
												className={`w-full flex flex-col items-center justify-center p-4 rounded-t-lg ${activeTab === 'callAssistant' ? '' : ''}`}
												id="call-assistant-tab"
												type="button"
												role="tab"
												aria-controls="call-assistant"
												aria-selected={activeTab === 'callAssistant'}
												onClick={() => handleTabClick('callAssistant')}
											>
												<Video className="mb-2 h-5 w-5" color={activeTab === 'callAssistant' ? '#2626E0' : '#757575'} />
												<span style={{ color: activeTab === 'callAssistant' ? '#2626E0' : '#757575' }}>Call Assistant</span>
											</button>
										</li>
									)}
								</ul>

							</div>
						</div>
						)}

						{/* {/* -----------------------------------------------------------Open ChatBox----------------------------------------------------} */}

						<div id="default-tab-content">
							<div className={`${canUseAssignedChatTabs && activeTab === 'chats' ? '' : 'hidden'} -mt-2 bg-white`} id="profile" role="tabpanel" aria-labelledby="profile-tab">
								<RecentChats
									openChatbox={openChatbox}
									selectedEmployeeId={selectedEmployeeId}
									setAllGroupMessages={setAllGroupMessages}
									setSelectedEmployeeId={setSelectedEmployeeId}
									setMyInputMessages={setMyInputMessages}
									setChatAppenedMessages={setChatAppenedMessages}
									setChatInput={setChatInput}
									employeesOnlineStatus={employeesOnlineStatus}
									allGroupMessages={allGroupMessages}
									chatInput={chatInput}
									myInputMessages={myInputMessages}
									chatAppenedMessages={chatAppenedMessages}
									openOverlay={openOverlay}
									closeOverlay={closeOverlay}
									recentChatLoading={recentChatLoading}
									playMessageSound={playMessageSound}
									setPage={setPage}
									page={page}
									inline
								/>
								{!chatDetailOpen && <div className='h-24'></div>}
							</div>

							<div className={`${canUseAssignedChatTabs && activeTab === 'contact' ? '' : 'hidden'} -mt-2 bg-white`} id="contact" role="tabpanel" aria-labelledby="contact-tab">
								<ContactList
									openChatbox={openChatbox}
									selectedEmployeeId={selectedEmployeeId}
									setAllGroupMessages={setAllGroupMessages}
									setSelectedEmployeeId={setSelectedEmployeeId}
									setMyInputMessages={setMyInputMessages}
									setChatAppenedMessages={setChatAppenedMessages}
									setChatInput={setChatInput}
									employeesOnlineStatus={employeesOnlineStatus}
									allGroupMessages={allGroupMessages}
									chatInput={chatInput}
									myInputMessages={myInputMessages}
									chatAppenedMessages={chatAppenedMessages}
									openOverlay={openOverlay}
									closeOverlay={closeOverlay}
									chatLoading={chatLoading}
									setPage={setPage}
									page={page}
									inline
								/>
								{!chatDetailOpen && <div className='h-24'></div>}
							</div>

							<div className={`${isSupportTabAvailable && activeTab === 'support' ? '' : 'hidden'} support-chat-tab-shell`} id="support" role="tabpanel" aria-labelledby="support-tab">
									<SupportChatPanel
									profileData={profileData}
									pharmacyLoginData={pharmacyLoginData}
									senderName={headerDisplayName}
									teamStatus={teamStatus}
									isActive={isChatboxVisible}
									refreshToken={supportRefreshToken}
									registerToolbar={setSupportToolbar}
									onSupportAvailabilityChange={setIsSupportTabAvailable}
									onUnreadCountChange={setSupportUnreadCount}
									unreadThreadIds={supportUnreadThreadIds}
									onUnreadThreadIdsChange={setSupportUnreadThreadIds}
									onMinimize={toggleChatbox}
								/>
								{!supportDetailOpen && <div className="h-14"></div>}
							</div>

							{isLoggedInPharmacist && (
								<div className={`${activeTab === 'callAssistant' ? '' : 'hidden'} -mt-2 bg-white`} id="call-assistant" role="tabpanel" aria-labelledby="call-assistant-tab">
									<PharmacistDriverVideoCallManager triggerPlacement="chat-tab" refreshKey={callAssistantRefreshKey} />
									<div className='h-24'></div>
								</div>
							)}
						</div>

						{/* --------------------------------------------------------------------Create a Chat Group Section Start----------------------------------------------- */}

						<CreateChatGroup />

						{/* --------------------------------------------------------------------Create a Chat Group End ----------------------------------------------- */}

					</div>
				</div>
			</section>

			{!isAssistantVideoCallActive && !isChatboxVisible &&
				<Draggable
					bounds="body"
					nodeRef={launcherNodeRef}
					position={launcherPosition}
					onStart={() => {
						launcherDraggedRef.current = false;
					}}
					onDrag={(_event, data) => {
						if (Math.abs(data.deltaX) + Math.abs(data.deltaY) > 2) {
							launcherDraggedRef.current = true;
						}
					}}
					onStop={(_event, data) => {
						setLauncherPosition({ x: data.x, y: data.y });
					}}
				>
				<button
					ref={launcherNodeRef}
					onClick={toggleChatbox}
					className="z-10 fixed lg:show hidden bottom-4 right-4 h-14 w-14 cursor-grab rounded-full text-white shadow-[0_18px_38px_rgba(38,38,224,0.32)] ring-1 ring-white/20 active:cursor-grabbing lg:block sm:block md:block"
					style={{
						background: 'linear-gradient(135deg, #2626E0 0%, #4338CA 100%)',
						// Pharmacist review dialogs use z-index values through 174. Keep the
						// launcher available without changing either dialog's layout or backdrop.
						zIndex: 180,
						touchAction: 'none',
					}}
					aria-label="Open chat"
				>
					<span className="flex h-full w-full items-center justify-center">
						<MessageSquareMore size={24} strokeWidth={2.1} />
					</span>
					{totalUnreadCount > 0 &&
						<label
							className='pointer-events-none'
							style={{
								color: 'white',
								position: 'absolute',
								top: '-2px',
								right: '-2px',
								display: 'inline-flex',
								alignItems: 'center',
								justifyContent: 'center',
								minWidth: '22px',
								height: '22px',
								padding: '0 6px',
								fontSize: '11px',
								fontWeight: '600',
								lineHeight: '1',
								borderRadius: '61px',
								background: '#ef4444',
								boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
							}}
						>
							{totalUnreadCount > 99 ? '99+' : totalUnreadCount}
						</label>}
				</button>
				</Draggable>}

			<audio id="messageSound">
				<source src={audio.chatAudio} type="audio/mpeg" />
			</audio>

			{/* {overlayImage &&
				<div id="overlay">
					<span id="close-btn" onClick={() => setOverlayImage(null)}>&times;</span>
					<img src={overlayImage} className="rounded-md" alt="" />
				</div>
			} */}
		</>
	)
}

export default ChatBox
